/**
 * HKDF-SHA-256 sub-key derivation via WebCrypto.
 *
 * Used for: UMK-proof MAC key (`info='umk-proof-v1'`, salt=userId) and device
 * KEK derivation. RFC-5869. The IKM is imported as a non-extractable HKDF key;
 * the derived bits are returned as raw bytes (the caller decides extractability
 * of any key it builds from them).
 */
import { toArrayBuffer } from './bytes.js';
const enc = new TextEncoder();
function ascii(s) {
    return enc.encode(s);
}
/** Raw HKDF-SHA-256 expand+extract. Returns `length` bytes. */
export async function hkdfSha256(args) {
    const key = await crypto.subtle.importKey('raw', toArrayBuffer(args.ikm), 'HKDF', false, [
        'deriveBits',
    ]);
    const bits = await crypto.subtle.deriveBits({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: toArrayBuffer(args.salt),
        info: toArrayBuffer(args.info),
    }, key, args.length * 8);
    return new Uint8Array(bits);
}
/**
 * Derive the 32-byte UMK-proof MAC key: `HKDF(UMK, info='umk-proof-v1', salt=userId)`.
 * Spec §8.
 */
export async function deriveUmkProofKey(umk, userId) {
    return hkdfSha256({ ikm: umk, salt: userId, info: ascii('umk-proof-v1'), length: 32 });
}
/**
 * Derive a 32-byte device KEK from an X25519 shared secret. `info` is the role
 * label (e.g. device id). Salt is a fixed service constant. Spec §4.
 */
export async function deriveDeviceKek(sharedSecret, info) {
    return hkdfSha256({
        ikm: sharedSecret,
        salt: ascii('heivol-locker-device-v1'),
        info,
        length: 32,
    });
}
//# sourceMappingURL=hkdf.js.map